import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { PrimaryButton } from '../../../components/Buttons';
import Drawer from '../../../components/Drawer';
import { Container } from '../../../components/Layout';
import useModal from '../../../hooks/useModal';
import { CurriculumBrand } from '../../curriculum';
import { CurriculumPricingEntity, Subscription } from '../../curriculum/models/curriculumPricing';
import { CurrentSubscriptionData } from '../SwitchCurriculum';
import { ConfirmSubscriptionChangesModal } from './ConfirmSubscriptionChangesModal';
import CurriculumCoverImage from './CurriculumCoverImage';

const DrawerContent = styled(Container)`
  position: relative;
  margin-bottom: ${Grid._3};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${Grid._5};
  }
`;

const SelectedItemsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._5};
  flex-wrap: wrap;

  @media (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    gap: ${Grid._4};
  }
`;

const SelectedOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._5};

  @media (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    gap: ${Grid._4};
  }
`;

const SelectedOptionIcon = styled.i`
  color: ${Color.Gray._50};

  @media (max-width: ${Breakpoints.screen_sm}) {
    display: none;
  }
`;

const SelectedOptionTitle = styled.h3`
  margin: 0;
  font-size: ${Type.Scale._4};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};

  @media (max-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._3};
    text-align: center;
  }
`;

const SelectedOptionSubtitle = styled.p`
  margin: ${Grid._2} 0 0 0;
  color: ${Color.Gray._70};
  font-size: ${Type.Scale._2};

  @media (max-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._1};
    text-align: center;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  margin-left: ${Grid._5};

  @media (max-width: ${Breakpoints.screen_sm}) {
    margin-left: 0;
    width: 100%;
  }
`;

const SelectedSubscriptionImage = styled(CurriculumCoverImage)`
  border-radius: ${Border.radius};
  width: 150px;

  @media (max-width: ${Breakpoints.screen_sm}) {
    display: none;
  }
`;

export default function SwitchCurriculumDrawer({
  currentSubscriptionData,
  selectedCurriculum,
  handleCloseDrawer,
  selectedSubscription,
  selectedMinistrySize,
}: {
  currentSubscriptionData?: CurrentSubscriptionData;
  selectedCurriculum?: { brand: CurriculumBrand; ageGroup: CurriculumPricingEntity };
  handleCloseDrawer: () => void;
  selectedMinistrySize?: CurriculumPricingEntity;
  selectedSubscription?: Subscription;
}) {
  const selectedPlan = selectedSubscription?.package?.toLowerCase().includes('plus') ? 'plus' : 'basic';
  const selectedBillingPeriod = selectedSubscription?.period;

  const [modal, openModal] = useModal((type: string, _: any, dismissModal: () => void) => {
    switch (type) {
      case 'changeSubscriptionOnRenewalDate':
        return (
          <ConfirmSubscriptionChangesModal
            currentSubscriptionData={currentSubscriptionData}
            selectedCurriculum={selectedCurriculum}
            selectedSubscription={selectedSubscription}
            selectedPlan={selectedPlan}
            selectedBillingPeriod={selectedBillingPeriod}
            handleDismiss={dismissModal}
          />
        );
      default:
        return null;
    }
  });

  return (
    <>
      <Drawer
        isOpen={!!selectedCurriculum}
        onClose={handleCloseDrawer}
        showBackdrop={false}
        showCloseButton={false}
        title={''}
      >
        <DrawerContent>
          <SelectedItemsContainer>
            <SelectedOption>
              {selectedCurriculum && (
                <>
                  <SelectedOption>
                    <SelectedSubscriptionImage brand={selectedCurriculum.brand} />
                    <div>
                      <SelectedOptionTitle>{selectedCurriculum.brand.name}</SelectedOptionTitle>
                      <SelectedOptionSubtitle>{selectedCurriculum.ageGroup.name}</SelectedOptionSubtitle>
                    </div>
                  </SelectedOption>
                </>
              )}
              {selectedMinistrySize && (
                <>
                  <SelectedOptionIcon className="fa fa-chevron-right" />
                  <div>
                    <SelectedOptionTitle>Ministry Size</SelectedOptionTitle>
                    <SelectedOptionSubtitle>{selectedMinistrySize.subscription[0].size}</SelectedOptionSubtitle>
                  </div>
                </>
              )}
              {selectedPlan && selectedBillingPeriod && (
                <>
                  <SelectedOptionIcon className="fa fa-chevron-right" />
                  <div>
                    <SelectedOptionTitle>Plan</SelectedOptionTitle>
                    <SelectedOptionSubtitle>
                      {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} (
                      {selectedBillingPeriod === 'year' ? 'Yearly' : 'Monthly'})
                    </SelectedOptionSubtitle>
                  </div>
                </>
              )}
            </SelectedOption>
          </SelectedItemsContainer>
          {selectedCurriculum &&
            selectedMinistrySize &&
            selectedSubscription &&
            selectedPlan &&
            selectedBillingPeriod && (
              <ActionButtonsContainer>
                <PrimaryButton onClick={() => openModal('changeSubscriptionOnRenewalDate')}>
                  Change Subscription on Renewal Date
                </PrimaryButton>
              </ActionButtonsContainer>
            )}
        </DrawerContent>
      </Drawer>
      {modal}
    </>
  );
}
